import { useState } from 'react';

import { Button } from '@components/button';
import { IconFormIndicatorInfo } from '@components/icons/form-indicator-info';
import { InfoBox } from '@components/info-box';
import { hideModal, Modal, useModal } from '@components/modal';
import { Stack } from '@components/stack';
import { TextArea } from '@components/textarea';
import { useScopedTranslate } from '@locale/translate';

import { AttributeFieldType } from 'api/attributes/requests';
import { AttributeIcon } from 'ui/authenticated/settings/pages/attributes/common';

export type AddFieldModalAttribute = {
  shortId: string;
  title: string;
  attributeType: AttributeFieldType;
};

type EditFieldModalProps = {
  icon: AttributeFieldType;
  title: string;
  shortId: string;
  initialInstructions?: string;
  onSubmit: (shortId: string, instructions: string) => void;
};

export const useEditFieldModal = () => {
  return useModal(EditFieldModal, { variant: 'REGULAR' });
};

/** @deprecated Use useEditFieldModal instead — field picking now happens via a dropdown in the sidebar */
export const useAddFieldModal = useEditFieldModal;

const EditFieldModal = ({
  icon,
  title,
  shortId,
  initialInstructions,
  onSubmit,
}: EditFieldModalProps) => {
  const { t } = useScopedTranslate(locale);
  const [instructions, setInstructions] = useState<string>(initialInstructions ?? '');

  const canSubmit = instructions.trim().length > 0 && instructions !== (initialInstructions ?? '');

  const handleSubmit = () => {
    if (!canSubmit) {
      return;
    }
    onSubmit(shortId, instructions);
    hideModal();
  };

  return (
    <Modal>
      <Modal.SimpleHeader
        title={title}
        icon={<AttributeIcon type={icon} color="elementPurple50" />}
      />
      <Modal.Content>
        <Stack space={4}>
          <InfoBox
            text={t('infoText')}
            variant="indigo"
            icon={<IconFormIndicatorInfo color="elementIndigo200" />}
            align="flex-start"
          />
          <TextArea
            label={t('instructionsLabel')}
            placeholder={t('instructionsPlaceholder')}
            value={instructions}
            onChange={(value) => {
              setInstructions(value);
            }}
            isExpandable
          />
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <Stack orientation="horizontal" justify="flex-end" space={2}>
          <Button variant="secondary" text={t('core.cancel')} onClick={hideModal} />
          <Button
            variant="primary-blue"
            text={t('core.save')}
            onClick={handleSubmit}
            isDisabled={!canSubmit}
          />
        </Stack>
      </Modal.Footer>
    </Modal>
  );
};

export const locale = {
  en: {
    infoText: 'Give the AI model instructions on how to generate this output from the email body',
    instructionsLabel: 'Instructions',
    instructionsPlaceholder: 'Add instructions...',
  },
};
